/*
 * Isabella · bloques personalizados (isa-*)
 * Se carga como módulo desde cada bloque que lo necesita; el navegador lo ejecuta una sola vez.
 */
import { ThemeEvents, CartAddEvent } from '@theme/events';

/* ------------------------------------------------------------------ */
/* Refresca un bloque cuando cambia la variante (ahorro, aviso de stock) */
/* ------------------------------------------------------------------ */
class IsaVariantSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (this.section) this.section.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  disconnectedCallback() {
    if (this.section) this.section.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  onVariantUpdate = (event) => {
    const html = event.detail && event.detail.data && event.detail.data.html;
    if (!html) return;
    const fresh = html.querySelector(`isa-variant-sync[data-block-id="${this.dataset.blockId}"]`);
    if (fresh) {
      this.innerHTML = fresh.innerHTML;
      this.hidden = fresh.hidden;
    }
  };
}
if (!customElements.get('isa-variant-sync')) customElements.define('isa-variant-sync', IsaVariantSync);

/* ------------------------------------------------------------------ */
/* Barra de progreso de la promo por volumen (lee el carrito)          */
/* ------------------------------------------------------------------ */
class IsaCartProgress extends HTMLElement {
  connectedCallback() {
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (e) {
      this.tiers = [];
    }
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  onCartUpdate = async (event) => {
    let cart = event.detail && event.detail.resource;
    if (!cart || !Array.isArray(cart.items)) {
      try {
        const res = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart.js`);
        cart = await res.json();
      } catch (e) {
        return;
      }
    }
    // El seguro de envío no cuenta como prenda
    const exclude = Number(this.dataset.excludeProduct || 0);
    const count = cart.items.reduce((n, item) => (item.product_id === exclude ? n : n + item.quantity), 0);
    this.render(count);
  };

  render(count) {
    const tiers = this.tiers;
    if (!tiers.length) return;
    let reached = null;
    let next = null;
    for (const tier of tiers) {
      if (count >= tier.qty) reached = tier;
      else if (!next) next = tier;
    }
    const unitFor = (n) => (n === 1 ? this.dataset.unitOne : this.dataset.unitMany);
    const fillReward = (msg, tier) => {
      if (!tier) return msg.split('[porcentaje]').join('').split('[premio]').join('');
      if (tier.label) {
        return msg
          .split('ahorra [porcentaje]% extra').join('consigue [premio]')
          .split('máximo descuento: [porcentaje]% extra').join('premio: [premio]')
          .split('[porcentaje]% extra').join('[premio]')
          .split('[porcentaje]%').join('[premio]')
          .split('[porcentaje]').join('[premio]')
          .split('[premio]').join(tier.label);
      }
      return msg.split('[premio]').join(`${tier.pct}% extra`).split('[porcentaje]').join(String(tier.pct));
    };
    let message;
    if (next) {
      const missing = next.qty - count;
      message = fillReward(this.dataset.msgNext, next)
        .split('[faltan]').join(String(missing))
        .split('[unidad]').join(unitFor(missing));
    } else {
      message = fillReward(this.dataset.msgMax, reached);
    }
    const msgEl = this.querySelector('[data-isa-message]');
    if (msgEl) msgEl.textContent = message;

    const segments = this.querySelectorAll('[data-isa-segment]');
    segments.forEach((segment) => {
      const qty = Number(segment.dataset.qty);
      segment.classList.toggle('is-active', count >= qty);
    });
    const fill = this.querySelector('[data-isa-fill]');
    if (fill) {
      const max = tiers[tiers.length - 1].qty;
      fill.style.width = `${Math.min(100, Math.round((count / max) * 100))}%`;
    }
  }
}
if (!customElements.get('isa-cart-progress')) customElements.define('isa-cart-progress', IsaCartProgress);

/* ------------------------------------------------------------------ */
/* Fecha estimada de entrega                                           */
/* ------------------------------------------------------------------ */
class IsaDelivery extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min || 0);
    const max = Number(this.dataset.max || 0);
    const skipWeekends = this.dataset.skipWeekends === 'true';
    const format = this.dataset.format || 'long';
    const base = new Date();
    const cutoffEl = this.querySelector('[data-isa-cutoff]');
    if (this.dataset.cutoff !== undefined && cutoffEl) {
      const hour = Number(this.dataset.cutoff);
      const target = new Date();
      target.setHours(hour, 0, 0, 0);
      if (Date.now() >= target.getTime()) {
        target.setDate(target.getDate() + 1);
        base.setDate(base.getDate() + 1);
      }
      const tick = () => {
        const diff = Math.max(0, target.getTime() - Date.now());
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        cutoffEl.textContent = h > 0 ? `${h} h ${m} min` : `${m} min`;
      };
      tick();
      this._cut = window.setInterval(tick, 30000);
    }
    const start = this.addDays(base, min, skipWeekends);
    const end = this.addDays(base, max, skipWeekends);
    const startEl = this.querySelector('[data-isa-start]');
    const endEl = this.querySelector('[data-isa-end]');
    if (startEl) startEl.textContent = this.formatDate(start, format);
    if (endEl) endEl.textContent = this.formatDate(end, format);
  }

  disconnectedCallback() {
    if (this._cut) window.clearInterval(this._cut);
  }

  addDays(date, days, skipWeekends) {
    const result = new Date(date.getTime());
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (skipWeekends && (day === 0 || day === 6)) continue;
      added += 1;
    }
    return result;
  }

  formatDate(date, format) {
    const locale = document.documentElement.lang || 'es-MX';
    const options =
      format === 'short'
        ? { weekday: 'short', day: 'numeric', month: 'short' }
        : format === 'weekday'
          ? { weekday: 'long', day: 'numeric', month: 'long' }
          : { day: 'numeric', month: 'long' };
    try {
      return date.toLocaleDateString(locale, options);
    } catch (e) {
      return date.toLocaleDateString('es-MX', options);
    }
  }
}
if (!customElements.get('isa-delivery')) customElements.define('isa-delivery', IsaDelivery);

/* ------------------------------------------------------------------ */
/* Botón "Agregar" de Completa tu look                                 */
/* ------------------------------------------------------------------ */
class IsaQuickAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button[data-isa-add]');
    this.select = this.querySelector('select');
    this.priceEl = this.querySelector('[data-isa-price]');
    if (this.button) this.button.addEventListener('click', this.onAdd);
    if (this.select) this.select.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onAdd);
    if (this.select) this.select.removeEventListener('change', this.onChange);
  }

  onChange = () => {
    const option = this.select.selectedOptions[0];
    if (!option) return;
    if (this.priceEl && option.dataset.price) this.priceEl.textContent = option.dataset.price;
    this.button.disabled = option.disabled;
  };

  onAdd = async () => {
    const variantId = this.select ? this.select.value : this.dataset.variantId;
    if (!variantId) return;
    const labelEl = this.button.querySelector('[data-isa-label]');
    const original = labelEl ? labelEl.textContent : '';
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    try {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Error');
      const cartRes = await fetch(`${root}cart.js`);
      const cart = await cartRes.json();
      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'isa-quick-add', {
          source: 'isa-quick-add',
          itemCount: 1,
          variantId: String(variantId),
        })
      );
      if (labelEl) labelEl.textContent = this.dataset.addedLabel || original;
      setTimeout(() => {
        if (labelEl) labelEl.textContent = original;
      }, 2500);
    } catch (error) {
      const errorEl = this.querySelector('[data-isa-error]');
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
        setTimeout(() => (errorEl.hidden = true), 6000);
      }
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
  };
}
if (!customElements.get('isa-quick-add')) customElements.define('isa-quick-add', IsaQuickAdd);

/* ------------------------------------------------------------------ */
/* Oculta elementos con fecha de fin vencida (la página puede estar en caché) */
/* ------------------------------------------------------------------ */
class IsaExpire extends HTMLElement {
  connectedCallback() {
    const end = this.dataset.end;
    if (!end) return;
    const endDate = new Date(`${end}T23:59:59`);
    if (!Number.isNaN(endDate.getTime()) && Date.now() > endDate.getTime()) this.hidden = true;
  }
}
if (!customElements.get('isa-expire')) customElements.define('isa-expire', IsaExpire);

/* ------------------------------------------------------------------ */
/* Pestañas (categorías Mujer / Hombre)                                */
/* ------------------------------------------------------------------ */
class IsaTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-isa-tab]'));
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab.dataset.isaTab)));
    if (this.tabs.length) this.select(this.tabs[0].dataset.isaTab);
    this.addEventListener('shopify:block:select', (event) => {
      const target = event.target;
      if (target && target.dataset && target.dataset.isaTab) return this.select(target.dataset.isaTab);
      const panel = target && target.closest && target.closest('[data-isa-panel]');
      if (panel) this.select(panel.dataset.isaPanel);
    });
  }

  select(id) {
    if (!this.tabs.length) return;
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.isaTab === id)));
    this.querySelectorAll('[data-isa-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.isaPanel !== id;
    });
  }
}
if (!customElements.get('isa-tabs')) customElements.define('isa-tabs', IsaTabs);

/* ------------------------------------------------------------------ */
/* Chips de filtros rápidos manuales: marca el chip de la URL actual    */
/* ------------------------------------------------------------------ */
class IsaChips extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-auto')) return;
    const here = window.location.pathname + window.location.search;
    this.querySelectorAll('[data-isa-chip]').forEach((chip) => {
      try {
        const url = new URL(chip.href, window.location.origin);
        const target = url.pathname + url.search;
        if (target === here || (!url.search && url.pathname === window.location.pathname && !window.location.search)) {
          chip.setAttribute('aria-current', 'true');
          chip.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
      } catch (e) {
        /* enlace no válido */
      }
    });
  }
}
if (!customElements.get('isa-chips')) customElements.define('isa-chips', IsaChips);

/* ------------------------------------------------------------------ */
/* Pestañas del bloque de categorías                                   */
/* ------------------------------------------------------------------ */
class IsaCatTabs extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-isa-tabnum]').forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.isaTabnum));
    });
    this.addEventListener('shopify:block:select', (event) => {
      const item = event.target && event.target.closest && event.target.closest('[data-isa-cat-tab]');
      if (item) this.select(item.dataset.isaCatTab);
    });
  }

  select(num) {
    this.dataset.active = num;
    this.querySelectorAll('[data-isa-tabnum]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.isaTabnum === num)));
  }
}
if (!customElements.get('isa-cat-tabs')) customElements.define('isa-cat-tabs', IsaCatTabs);

/* ------------------------------------------------------------------
 * <isa-proof-gallery>: galería de clientes con autoplay + reseñas con puntos.
 * ------------------------------------------------------------------ */
class IsaProofGallery extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.gallery = this.querySelector('[data-gallery]');
    this.reviews = this.querySelector('[data-reviews]');
    this.dotsWrap = this.querySelector('[data-dots]');
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.setupGallery();
    this.setupReviews();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this._onVis);
  }

  scrollTo(track, item, behavior = 'smooth') {
    if (!track || !item) return;
    track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: this.reduce ? 'auto' : behavior });
  }

  setupGallery() {
    const items = this.gallery ? Array.from(this.gallery.children) : [];
    const speed = Number(this.dataset.speed || 0) * 1000;
    if (!this.gallery || items.length < 2 || !speed || this.reduce) return;
    let i = 0;
    this.start = () => {
      this.stop();
      if (this._touched || document.hidden) return;
      this._timer = window.setInterval(() => {
        const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;
        i = this.gallery.scrollLeft >= maxScroll - 2 ? 0 : i + 1;
        if (i >= items.length) i = 0;
        this.scrollTo(this.gallery, items[i]);
      }, speed);
    };
    ['pointerdown', 'touchstart', 'wheel'].forEach((ev) =>
      this.gallery.addEventListener(ev, () => { this._touched = true; this.stop(); }, { passive: true, once: true })
    );
    this._onVis = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this._onVis);
    this.start();
  }

  stop() {
    if (this._timer) window.clearInterval(this._timer);
    this._timer = null;
  }

  setupReviews() {
    const cards = this.reviews ? Array.from(this.reviews.children) : [];
    if (!this.dotsWrap || cards.length < 2) return;
    this.dotsWrap.innerHTML = '';
    const dots = cards.map((card, idx) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'isa-proof__dot';
      b.setAttribute('aria-label', `Ver reseña ${idx + 1}`);
      b.addEventListener('click', () => { this.scrollTo(this.reviews, card); set(idx); });
      this.dotsWrap.appendChild(b);
      return b;
    });
    const set = (n) => dots.forEach((d, k) => { d.classList.toggle('is-active', k === n); d.setAttribute('aria-current', k === n ? 'true' : 'false'); });
    let raf = 0;
    this.reviews.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        let best = 0, dist = Infinity;
        cards.forEach((c, k) => {
          const d = Math.abs(c.offsetLeft - this.reviews.offsetLeft - this.reviews.scrollLeft);
          if (d < dist) { dist = d; best = k; }
        });
        set(best);
      });
    }, { passive: true });
    set(0);
  }
}
if (!customElements.get('isa-proof-gallery')) customElements.define('isa-proof-gallery', IsaProofGallery);

/* ------------------------------------------------------------------
 * <isa-rotator data-rotate data-interval="3">: muestra un hijo cada vez (solo móvil).
 * ------------------------------------------------------------------ */
class IsaRotator extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-rotate') || this._t) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const items = Array.from(this.children);
    if (items.length < 2) return;
    let i = 0;
    const ms = Math.max(2, Number(this.dataset.interval || 3)) * 1000;
    this._t = window.setInterval(() => {
      if (document.hidden || window.innerWidth >= 750) return;
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, ms);
  }
  disconnectedCallback() {
    window.clearInterval(this._t);
    this._t = null;
  }
}
if (!customElements.get('isa-rotator')) customElements.define('isa-rotator', IsaRotator);

/* ------------------------------------------------------------------
 * <isa-countdown data-end="ISO">: días / horas / min / seg; se oculta al terminar.
 * ------------------------------------------------------------------ */
class IsaCountdown extends HTMLElement {
  connectedCallback() {
    const raw = this.dataset.end || '';
    // Acepta 2026-11-17T23:59:00-0600 (Liquid %z) y lo normaliza a -06:00
    const iso = raw.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    this.end = new Date(iso).getTime();
    if (Number.isNaN(this.end)) return;
    this.els = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
    this.tick();
    this._t = window.setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() {
    window.clearInterval(this._t);
  }
  tick() {
    const diff = this.end - Date.now();
    if (diff <= 0) {
      this.hidden = true;
      window.clearInterval(this._t);
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    if (this.els.d) this.els.d.textContent = pad(d);
    if (this.els.h) this.els.h.textContent = pad(h);
    if (this.els.m) this.els.m.textContent = pad(m);
    if (this.els.s) this.els.s.textContent = pad(s);
  }
}
if (!customElements.get('isa-countdown')) customElements.define('isa-countdown', IsaCountdown);

/* ------------------------------------------------------------------
 * <isa-shipping-protection>: casilla que añade / quita el seguro de envío.
 * ------------------------------------------------------------------ */
class IsaShippingProtection extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    if (!this.input) return;
    this.input.addEventListener('change', this.onToggle);
    // Las acciones automáticas solo se hacen cuando la casilla se ve (carrito abierto),
    // para no abrir el carrito lateral al cargar cualquier página.
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this.observer.disconnect();
        this.autoSync();
      }
    });
    this.observer.observe(this);
  }

  autoSync() {
    const inCart = this.dataset.inCart === 'true';
    const others = Number(this.dataset.others || 0);
    let declined = false;
    let auto = false;
    try {
      declined = sessionStorage.getItem('isa_sprot_declined') === '1';
      auto = sessionStorage.getItem('isa_sprot_auto') === '1';
    } catch (e) {}
    // Si solo queda el seguro en el carrito, se quita.
    if (inCart && others === 0) {
      this.update(false);
      return;
    }
    // Marcado por defecto: se añade una sola vez por sesión.
    if (!inCart && others > 0 && this.dataset.defaultOn === 'true' && !declined && !auto) {
      try { sessionStorage.setItem('isa_sprot_auto', '1'); } catch (e) {}
      this.input.checked = true;
      this.update(true);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.input) this.input.removeEventListener('change', this.onToggle);
  }

  onToggle = () => {
    const on = this.input.checked;
    try {
      sessionStorage.setItem('isa_sprot_declined', on ? '0' : '1');
    } catch (e) {}
    this.update(on);
  };

  async update(on) {
    if (this._busy) return;
    this._busy = true;
    this.setAttribute('aria-busy', 'true');
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    const id = Number(this.dataset.variantId);
    try {
      if (on) {
        await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
      } else {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: { [id]: 0 } }),
        });
      }
      const cart = await (await fetch(`${root}cart.js`)).json();
      document.dispatchEvent(
        new CartAddEvent(cart, 'isa-shipping-protection', {
          source: 'isa-shipping-protection',
          itemCount: cart.items.reduce((n, item) => (item.variant_id === id ? n : n + item.quantity), 0),
          variantId: String(id),
        })
      );
    } catch (e) {
      if (this.input) this.input.checked = !on;
    } finally {
      this._busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('isa-shipping-protection')) customElements.define('isa-shipping-protection', IsaShippingProtection);

/* ------------------------------------------------------------------
 * <isa-grid-banner>: mete una imagen a todo el ancho dentro de la cuadrícula de productos.
 * Se recoloca sola cuando la cuadrícula cambia (filtros, ordenar, cargar más).
 * ------------------------------------------------------------------ */
class IsaGridBanner extends HTMLElement {
  connectedCallback() {
    this.tpl = this.querySelector('template');
    if (!this.tpl) return;
    this.place = this.place.bind(this);
    this.mq = window.matchMedia('(min-width: 750px)');
    this.mq.addEventListener('change', this.place);
    this.observer = new MutationObserver(() => {
      if (this._placing) return;
      window.requestAnimationFrame(this.place);
    });
    this.place();
    document.addEventListener('DOMContentLoaded', this.place);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mq?.removeEventListener('change', this.place);
  }

  grid() {
    return document.querySelector('.product-grid');
  }

  place() {
    const grid = this.grid();
    if (!grid) return;
    this._placing = true;
    this.observer.disconnect();
    grid.querySelectorAll(':scope > .isa-gridban[data-isa-from="' + this.id + '"]').forEach((el) => el.remove());
    const after = Number(this.mq.matches ? this.dataset.after : this.dataset.afterMobile) || 4;
    const repeat = this.dataset.repeat === 'true';
    const items = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    items.forEach((item, i) => {
      const n = i + 1;
      if (n === after || (repeat && n % after === 0 && n < items.length)) {
        const node = this.tpl.content.firstElementChild.cloneNode(true);
        node.dataset.isaFrom = this.id;
        item.after(node);
      }
    });
    this.observer.observe(grid, { childList: true });
    this._placing = false;
  }
}
if (!customElements.get('isa-grid-banner')) customElements.define('isa-grid-banner', IsaGridBanner);
